// «Внести часы» и «Пересчитать ведомость»: что делать с часами человека прямо в строке пробела.
//
// ## Откуда взялось
//
// У уволенного нет табеля за последний месяц, и финансовый директор заказчика просил «вручную
// можем добавить»: внести часы итогом, не уходя в табель объекта. До этого плашка «Табель за
// сентябрь не сдан» вела на экран табеля, где итог вводили по каждому человеку, и единственный
// такой ввод переключал способ ведения всего табеля (см. `services/timesheet_person_hours.py`).
//
// ## Путь человека
//
// Строка «Сентябрь 2026, «Заря»: табель не сдан»: справа поле часов с нормой внутри («из нормы
// 372 ч») и кнопка «Внести часы». Число проверяется до отправки, и больше нормы только
// предупреждает: человек мог отработать сверх неё. Нажатие открывает подтверждение словами
// («Внести 60 ч из 372 за сентябрь 2026 по объекту «Заря»?»), и только оно отправляет. Дальше
// строка меняется сама: «часы внесены в табель, 60 ч» и кнопка «Пересчитать ведомость». Деньги не
// меняются, пока человек не нажал её: документ не двигает суммы тихо.
//
// Поле стоит в строке одним рядом с кнопкой, а подпись у него невидимая (названа для чтения с
// экрана вместе с месяцем и объектом): шесть пробелов, у каждого своя подпись «Часы за месяц
// итогом», были шестью лишними строками, и таблица с деньгами уходила за два экрана.
//
// ## Что нельзя, и почему сказано заранее
//
// Нет права: поля и кнопки нет, а над списком пробелов сказано, кто вносит часы (один раз, а не
// в каждой строке). Ведомость утверждена, у человека несколько назначений на объекте, в табеле
// уже часы по сменам: поля нет, а под строкой причина словами сервера. Кнопка, до которой нажатие
// не доведёт, не должна ждать нажатия, чтобы сказать «нельзя».
//
// ## Ключ операции
//
// Живёт, пока живы те же часы: повтор после обрыва связи и двойное нажатие приходят с тем же
// ключом и возвращают прежний результат. Поменяли число после неудачи (а прежняя попытка могла
// дойти) — ключ новый, иначе сервер ответил бы «ключ с другим содержимым» там, где человек просто
// исправил число.

import { useRef, useState } from 'react';

import { RiErrorWarningLine, RiRefreshLine, RiTimeLine } from '@remixicon/react';

import type { PayslipPeriodGap } from '@/shared/api/endpoints/payslip';
import { newIdempotencyKey } from '@/shared/api/idempotency';
import { useAuthStore } from '@/shared/auth/store';
import { can } from '@/shared/rbac/permissions';
import { formatPeriodTitle } from '@/shared/time/period';
import * as Button from '@/shared/ui/button';
import { ErrorAlert } from '@/shared/ui/error-alert';
import * as Hint from '@/shared/ui/hint';
import * as Input from '@/shared/ui/input';
import * as Label from '@/shared/ui/label';
import * as LinkButton from '@/shared/ui/link-button';
import * as Modal from '@/shared/ui/modal';

import { useRecalculate } from '../sheets/queries';

import { useВнестиЧасы } from './queries';
import {
  большеНормы,
  разобратьЧасы,
  суффиксНормы,
  фразаПодтверждения,
  часыТекстом,
} from './тексты';

/** Вносит тот же, кто вводит табель: администратор, финансовый отдел, табельщик. */
const ПРАВО_ВНОСИТЬ = 'core.timesheet.edit';

/** Пересчёт ведомости, как и прежде: право на правку ведомостей. */
const ПРАВО_ПЕРЕСЧИТАТЬ = 'core.payroll.edit';

/** Фраза для того, у кого поля нет: кто это делает. Дословно из задания заказчика. */
export const КТО_ВНОСИТ = 'Вносить часы могут табельщик и финансовый отдел.';

export const КТО_ПЕРЕСЧИТЫВАЕТ = 'Ведомость пересчитывает финансовый отдел.';

/**
 * Что заденет пересчёт. Сказано один раз над списком пробелов, а не под каждой кнопкой: кнопок
 * бывает несколько, а предупреждение у них одно и то же.
 */
export const ПОЯСНЕНИЕ_ПЕРЕСЧЁТА =
  'Пересчитывается вся ведомость объекта: суммы у всех людей в ней обновятся по табелю.';

/**
 * Рисуется ли поле «Часы за месяц итогом»: сервер не назвал причину отказа, право есть и есть
 * объект, куда вносить. Строка пробела по нему же решает, оставлять ли ссылку «Открыть табель»
 * ссылкой: две одинаковые кнопки в одной строке не говорят, какая из них обычная.
 */
export function можноВнестиЧасы(пробел: PayslipPeriodGap, права: readonly string[]): boolean {
  return (
    (пробел.entry_blocked_reason === null || пробел.entry_blocked_reason === undefined) &&
    can(права, ПРАВО_ВНОСИТЬ) &&
    пробел.site_id !== null &&
    пробел.site_id !== undefined
  );
}

/**
 * Почему поля нет, словами: заранее, а не после нажатия. Серверная причина (ведомость утверждена,
 * несколько назначений, в табеле смены) называет месяц и объект и стоит под своей строкой. «Нет
 * права» одинаково для всех строк и сказано один раз над списком. `null` — причины нет: поле
 * есть либо вносить некуда (объекта нет, выдуманной формы не рисуем).
 */
export function причинаОтказа(
  пробел: PayslipPeriodGap,
  права: readonly string[],
): { сервер: string } | { право: true } | null {
  if (пробел.entry_blocked_reason !== null && пробел.entry_blocked_reason !== undefined) {
    return { сервер: пробел.entry_blocked_reason };
  }
  if (!can(права, ПРАВО_ВНОСИТЬ)) {
    return { право: true };
  }
  return null;
}

/**
 * Поле «Часы за месяц итогом» в строке пробела.
 *
 * Состояние зависит от пробела: часов в табеле нет (поле открыто сразу) либо они уже внесены
 * (поле за ссылкой «Изменить часы»: исправить число можно, но случайно задеть нельзя). Когда
 * вносить нельзя, ничего не рисуется: причину говорит строка пробела (`причинаОтказа`).
 */
export function ВнестиЧасы({
  пробел,
  employeeId,
}: {
  пробел: PayslipPeriodGap;
  employeeId: string;
}) {
  const права = useAuthStore((состояние) => состояние.permissions);
  const [раскрыто, setРаскрыто] = useState(false);
  const внесены = пробел.timesheet_hours !== null && пробел.timesheet_hours !== undefined;

  if (!можноВнестиЧасы(пробел, права) || пробел.site_id === null || пробел.site_id === undefined) {
    return null;
  }
  if (внесены && !раскрыто) {
    return (
      <LinkButton.Root
        variant='primary'
        size='small'
        underline
        type='button'
        className='self-center'
        onClick={() => setРаскрыто(true)}
      >
        Изменить часы
      </LinkButton.Root>
    );
  }
  return (
    <ФормаЧасов
      пробел={пробел}
      siteId={пробел.site_id}
      employeeId={employeeId}
      onГотово={() => setРаскрыто(false)}
    />
  );
}

function ФормаЧасов({
  пробел,
  siteId,
  employeeId,
  onГотово,
}: {
  пробел: PayslipPeriodGap;
  siteId: string;
  employeeId: string;
  onГотово: () => void;
}) {
  const период = { year: пробел.period_year, month: пробел.period_month };
  const прежние = пробел.timesheet_hours ?? null;
  const [текст, setТекст] = useState(прежние === null ? '' : часыТекстом(прежние));
  const [ошибка, setОшибка] = useState<string | undefined>(undefined);
  const [открыто, setОткрыто] = useState(false);
  const внести = useВнестиЧасы();
  // Ключ живёт, пока живы те же часы: см. шапку файла.
  const ключ = useRef<{ часы: string; ключ: string } | null>(null);
  // Засов от второй отправки. Ref, а не состояние: между двумя нажатиями подряд перерисовки нет.
  const идёт = useRef(false);

  const разбор = разобратьЧасы(текст, период);
  const сверхНормы = 'часы' in разбор && большеНормы(разбор.часы, пробел.norm_hours);
  const название = пробел.site_name ?? 'объект';

  function проверить() {
    if ('ошибка' in разбор) {
      setОшибка(разбор.ошибка);
      return;
    }
    setОшибка(undefined);
    внести.reset();
    setОткрыто(true);
  }

  function отправить() {
    if (идёт.current) {
      return;
    }
    if ('ошибка' in разбор) {
      setОткрыто(false);
      setОшибка(разбор.ошибка);
      return;
    }
    const часы = разбор.часы;
    if (ключ.current?.часы !== часы) {
      ключ.current = { часы, ключ: newIdempotencyKey() };
    }
    идёт.current = true;
    внести.mutate(
      { siteId, employeeId, период, часы, ключ: ключ.current.ключ },
      {
        onSuccess: () => {
          setОткрыто(false);
          ключ.current = null;
          onГотово();
        },
        onSettled: () => {
          идёт.current = false;
        },
      },
    );
  }

  const идПоля = `hours-${siteId}-${период.year}-${период.month}`;
  const идОшибки = `${идПоля}-error`;

  return (
    <form
      className='flex min-w-0 flex-col gap-1'
      noValidate
      onSubmit={(событие) => {
        событие.preventDefault();
        проверить();
      }}
    >
      {/* Поле и кнопка в одном ряду и при самой узкой ширине: кнопка, ушедшая вниз, повисала бы
          на расстоянии от поля и читалась как чужая. Подпись поля невидимая, но у каждого поля
          своя (с месяцем и объектом): полей в блоке бывает до десятка, и «Часы за месяц итогом»
          десять раз подряд читалось бы вслух как одно и то же. Норма стоит внутри поля, там же,
          где вводят число, а слово «часы» стоит на кнопке рядом: подсказка внутри пустого поля
          отнимала у числа место, а на планшете в портрете его и так ровно на шесть знаков. */}
      <div className='flex items-start gap-2'>
        <div className='w-40 min-w-0 shrink'>
          <Label.Root htmlFor={идПоля} className='sr-only'>
            Часы за месяц итогом: {formatPeriodTitle(период).toLowerCase()}, {название}
          </Label.Root>
          <Input.Root size='xsmall' hasError={ошибка !== undefined}>
            <Input.Wrapper>
              <Input.Input
                id={идПоля}
                value={текст}
                onChange={(событие) => {
                  setТекст(событие.target.value);
                  setОшибка(undefined);
                }}
                inputMode='decimal'
                autoComplete='off'
                maxLength={12}
                aria-invalid={ошибка !== undefined}
                aria-describedby={ошибка === undefined ? undefined : идОшибки}
              />
              {/* Норма стоит и у пустого поля, и у заполненного: подсказки в поле нет, поэтому бледным
                  («пустое поле») её базовый цвет не делается, а читать её человек обязан. */}
              <Input.InlineAffix className='whitespace-nowrap text-paragraph-xs'>
                {суффиксНормы(пробел.norm_hours)}
              </Input.InlineAffix>
            </Input.Wrapper>
          </Input.Root>
        </div>
        <Button.Root
          type='submit'
          variant='neutral'
          mode='stroke'
          size='xsmall'
          className='shrink-0'
        >
          {прежние === null ? 'Внести часы' : 'Заменить часы'}
        </Button.Root>
      </div>
      {ошибка !== undefined && (
        <Hint.Root hasError id={идОшибки}>
          <Hint.Icon as={RiErrorWarningLine} />
          {ошибка}
        </Hint.Root>
      )}
      {сверхНормы && ошибка === undefined && (
        // Предупреждение, а не ошибка: человек мог отработать сверх нормы, и внести можно.
        // Цвет несёт значок, а слова остаются обычными: оранжевый мелкий текст на оранжевой плашке
        // читается плохо.
        <p role='status' className='flex items-center gap-1 text-paragraph-xs text-text-sub-600'>
          <RiErrorWarningLine className='size-4 shrink-0 text-warning-base' />
          Больше нормы: проверьте число. Внести можно.
        </p>
      )}

      <Modal.Root
        open={открыто}
        onOpenChange={(следующее) => {
          // Пока запрос идёт, окно не закрывается: закрытое окно скрыло бы ошибку, а запрос
          // дошёл бы до сервера без ведома человека.
          if (внести.isPending) {
            return;
          }
          setОткрыто(следующее);
          if (!следующее) {
            внести.reset();
          }
        }}
      >
        <Modal.Content className='max-w-md'>
          {/* Вопрос с итоговой цифрой стоит заголовком: человек решает по числу, а не по слову
              «подтвердите». Остальное под ним объясняет, что изменится и что нет. */}
          <Modal.Header
            icon={RiTimeLine}
            title={
              'часы' in разбор
                ? фразаПодтверждения({
                    часы: разбор.часы,
                    норма: пробел.norm_hours,
                    прежние,
                    период,
                    объект: название,
                  })
                : 'Внести часы?'
            }
          />
          <Modal.Body>
            <ul className='flex flex-col gap-1.5 text-paragraph-sm text-text-sub-600'>
              <li>
                Часы запишутся в табель объекта итогом за месяц. Способ ведения табеля и часы других
                людей не меняются.
              </li>
              <li>
                Деньги в расчёте пока не изменятся: ведомость пересчитывается отдельной кнопкой.
              </li>
              {сверхНормы && <li>Это больше нормы: проверьте число перед тем, как вносить.</li>}
            </ul>
            {внести.isError && <ErrorAlert error={внести.error} className='mt-3' />}
          </Modal.Body>
          <Modal.Footer>
            <Modal.Close asChild>
              <Button.Root
                variant='neutral'
                mode='stroke'
                size='small'
                type='button'
                className='w-full'
              >
                Отмена
              </Button.Root>
            </Modal.Close>
            <Button.Root
              variant='primary'
              mode='filled'
              size='small'
              type='button'
              className='w-full'
              onClick={отправить}
            >
              {внести.isPending ? 'Вносим…' : прежние === null ? 'Внести часы' : 'Заменить часы'}
            </Button.Root>
          </Modal.Footer>
        </Modal.Content>
      </Modal.Root>
    </form>
  );
}

/**
 * «Пересчитать ведомость»: часы внесены, и деньги встанут по ним, когда ведомость пересчитают.
 *
 * Отдельная кнопка, а не побочное действие внесения: документ не должен менять суммы тихо.
 * Пересчёт обновляет ВСЮ ведомость, и об этом сказано один раз над списком пробелов
 * (`ПОЯСНЕНИЕ_ПЕРЕСЧЁТА`). Без права кнопки нет: кто пересчитывает, сказано там же.
 */
export function ПересчитатьВедомость({ payrollId }: { payrollId: string }) {
  const права = useAuthStore((состояние) => состояние.permissions);
  const пересчёт = useRecalculate(payrollId);

  if (!can(права, ПРАВО_ПЕРЕСЧИТАТЬ)) {
    return null;
  }
  return (
    <div className='flex min-w-0 flex-col items-start gap-1.5'>
      <Button.Root
        type='button'
        variant='neutral'
        mode='stroke'
        size='xsmall'
        onClick={() => {
          if (!пересчёт.isPending) {
            пересчёт.mutate();
          }
        }}
      >
        <Button.Icon as={RiRefreshLine} />
        {пересчёт.isPending ? 'Пересчитываем…' : 'Пересчитать ведомость'}
      </Button.Root>
      {пересчёт.isError && <ErrorAlert error={пересчёт.error} />}
    </div>
  );
}

/** Можно ли пересчитывать ведомости: по этому праву решает и строка пробела, и общий текст над ними. */
export function можноПересчитать(права: readonly string[]): boolean {
  return can(права, ПРАВО_ПЕРЕСЧИТАТЬ);
}
